import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { AlertTriangle, ArrowLeft, CheckCircle2, ChevronDown, ChevronRight, Loader2, RefreshCw, Scale } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { trpc } from '@/lib/trpc';
import { cn } from '@/lib/utils';
import { useAppStore } from '@/stores/app-store';
import { showErrorToast, showSuccessToast } from '@/hooks/use-mutation-with-toast';
import { Difference, Money, Stat } from '@/components/accounting/tie-out/money';
import { PartiesTable } from '@/components/accounting/tie-out/parties-table';
import { UnmirroredTable } from '@/components/accounting/tie-out/unmirrored-table';
import { StockCard } from '@/components/accounting/tie-out/stock-card';
import {
  defaultRange,
  isMismatched,
  isValidRange,
  utcDate,
  type DateRangeDraft,
  type TieOutApp,
} from '@/components/accounting/tie-out/model';

interface TieOutPageProps {
  app: TieOutApp;
}

/**
 * Mutabakat — the accountant's proof that the subsidiary ledgers agree with
 * the GL: cari balances vs 120/320 control accounts per counterparty, the
 * posted entries whose subledger mirror is missing (with a one-click,
 * idempotent repair), and for trading the 153 inventory account vs the FIFO
 * layers.
 */
export function TieOutPage({ app }: TieOutPageProps) {
  const { t } = useTranslation('tieOut');
  const today = useMemo(() => new Date(), []);
  const [draft, setDraft] = useState<DateRangeDraft>(() => defaultRange(today));
  const [range, setRange] = useState<DateRangeDraft>(draft);
  const [showAll, setShowAll] = useState(false);
  const [showLegacy, setShowLegacy] = useState(false);

  const canEdit = useAppStore((s) => s.hasPermissionForApp(app, 'ACCOUNTING', 'EDIT'));
  const utils = trpc.useUtils();
  const input = useMemo(() => ({ app, from: utcDate(range.from), to: utcDate(range.to) }), [app, range]);
  const cari = trpc.accountingTieOut.cari.useQuery(input);
  const stock = trpc.accountingTieOut.stock.useQuery(undefined, { enabled: app === 'TRADING' });
  const remirror = trpc.accountingTieOut.remirror.useMutation({
    onSuccess: (result) => {
      showSuccessToast(t('toast.repaired', { entries: result.entries, rows: result.rows }));
      utils.accountingTieOut.cari.invalidate();
    },
    onError: (e) => showErrorToast(e.message),
  });

  const report = cari.data;
  const visibleParties = useMemo(() => (report ? (showAll ? report.parties : report.parties.filter(isMismatched)) : []), [report, showAll]);
  const tied = report ? report.totals.mismatched === 0 && report.unmirrored.repairable.length === 0 : false;
  const hubHref = app === 'TRAVEL' ? '/travel/accounting' : '/trading/accounting';

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <Link to={hubHref} className="mb-1 inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground">
            <ArrowLeft className="h-3.5 w-3.5" />
            {t('backToAccounting')}
          </Link>
          <h1 className="flex items-center gap-2 text-2xl font-bold">
            <Scale className="h-6 w-6" />
            {t('title')}
          </h1>
          <p className="text-muted-foreground">{t(app === 'TRAVEL' ? 'subtitleTravel' : 'subtitleTrading')}</p>
        </div>

        <form
          className="flex flex-wrap items-end gap-2"
          onSubmit={(e) => {
            e.preventDefault();
            if (isValidRange(draft)) setRange(draft);
          }}
        >
          <label className="text-xs text-muted-foreground">
            {t('filters.from')}
            <Input type="date" value={draft.from} onChange={(e) => setDraft({ ...draft, from: e.target.value })} className="mt-1 h-8 w-[150px]" />
          </label>
          <label className="text-xs text-muted-foreground">
            {t('filters.to')}
            <Input type="date" value={draft.to} onChange={(e) => setDraft({ ...draft, to: e.target.value })} className="mt-1 h-8 w-[150px]" />
          </label>
          <Button type="submit" variant="outline" className="h-8" disabled={!isValidRange(draft) || cari.isFetching}>
            {cari.isFetching ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <RefreshCw className="mr-2 h-4 w-4" />}
            {t('filters.apply')}
          </Button>
        </form>
      </div>

      {cari.isLoading && (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      )}
      {cari.error && <div className="border border-destructive/50 bg-destructive/10 p-4 text-sm text-destructive">{cari.error.message}</div>}

      {report && (
        <>
          <div
            className={cn(
              'flex items-start gap-2 rounded-md border p-3 text-sm',
              tied
                ? 'border-emerald-300 bg-emerald-50 text-emerald-900 dark:border-emerald-800 dark:bg-emerald-900/20 dark:text-emerald-100'
                : 'border-red-300 bg-red-50 text-red-900 dark:border-red-800 dark:bg-red-900/20 dark:text-red-100'
            )}
          >
            {tied ? <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0" /> : <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />}
            <span>
              {tied
                ? t('balanced')
                : t('unbalanced', { parties: report.totals.mismatched, entries: report.unmirrored.repairable.length })}
            </span>
          </div>

          <div className="grid gap-2 sm:grid-cols-3 lg:grid-cols-6">
            <Stat label={t('summary.gl')} value={<Money value={report.totals.glBalance} />} />
            <Stat label={t('summary.cari')} value={<Money value={report.totals.cariBalance} />} />
            <Stat
              label={t('summary.difference')}
              value={<Difference value={report.totals.difference} />}
              tone={Math.abs(report.totals.difference) <= 0.01 ? 'emerald' : 'red'}
            />
            <Stat label={t('summary.parties')} value={report.totals.parties} />
            <Stat label={t('summary.mismatched')} value={report.totals.mismatched} tone={report.totals.mismatched > 0 ? 'red' : 'emerald'} />
            <Stat
              label={t('summary.repairable')}
              value={report.unmirrored.repairable.length}
              tone={report.unmirrored.repairable.length > 0 ? 'amber' : 'emerald'}
            />
          </div>

          <section className="rounded-md border border-border">
            <header className="flex flex-wrap items-center justify-between gap-2 border-b border-border px-4 py-3">
              <h2 className="text-base font-semibold">{t('parties.title')}</h2>
              <div className="flex items-center gap-1 text-xs">
                <Button variant={showAll ? 'ghost' : 'secondary'} size="sm" className="h-7 px-2 text-xs" onClick={() => setShowAll(false)}>
                  {t('parties.showMismatched', { count: report.totals.mismatched })}
                </Button>
                <Button variant={showAll ? 'secondary' : 'ghost'} size="sm" className="h-7 px-2 text-xs" onClick={() => setShowAll(true)}>
                  {t('parties.showAll', { count: report.totals.parties })}
                </Button>
              </div>
            </header>
            <PartiesTable app={app} parties={visibleParties} />
          </section>

          <section className="rounded-md border border-border">
            <header className="flex flex-wrap items-center justify-between gap-2 border-b border-border px-4 py-3">
              <div>
                <h2 className="text-base font-semibold">{t('unmirrored.title')}</h2>
                <p className="text-xs text-muted-foreground">{t('unmirrored.desc')}</p>
              </div>
              {report.unmirrored.repairable.length > 0 &&
                (canEdit ? (
                  <Button
                    size="sm"
                    className="h-8"
                    disabled={remirror.isPending}
                    onClick={() => remirror.mutate({ ...input, entryIds: report.unmirrored.repairable.map((e) => e.entryId) })}
                  >
                    {remirror.isPending ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <RefreshCw className="mr-2 h-4 w-4" />}
                    {remirror.isPending ? t('unmirrored.repairing') : t('unmirrored.repairAll', { count: report.unmirrored.repairable.length })}
                  </Button>
                ) : (
                  <span className="text-xs text-muted-foreground">{t('unmirrored.readOnly')}</span>
                ))}
            </header>
            <UnmirroredTable app={app} entries={report.unmirrored.repairable} />

            {report.unmirrored.legacy.length > 0 && (
              <div className="border-t border-border">
                <button
                  type="button"
                  className="flex w-full items-center gap-2 px-4 py-2 text-left text-sm text-muted-foreground hover:text-foreground"
                  onClick={() => setShowLegacy((v) => !v)}
                >
                  {showLegacy ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                  {t('unmirrored.legacyTitle', { count: report.unmirrored.legacy.length })}
                </button>
                {showLegacy && (
                  <>
                    <p className="px-4 pb-2 text-xs text-muted-foreground">{t('unmirrored.legacyDesc')}</p>
                    <UnmirroredTable app={app} entries={report.unmirrored.legacy} showReason />
                  </>
                )}
              </div>
            )}
          </section>
        </>
      )}

      {app === 'TRADING' && <StockCard report={stock.data} loading={stock.isLoading} error={stock.error?.message} />}
    </div>
  );
}
